import * as THREE from 'three';

import { HTMLLabel } from '../objects/html-label';
import LayerHTML from '../misc/html/html.layer';

const defaultObj = {
  width: window.innerWidth,
  height: window.innerHeight,
  siblingCamera: null
};

const RED = "0xff0000";
const GREEN = "0x00ff00";
const BLUE = "0x00ffff"; // "0x0000ff";

const conusSize = 15;

export default class Axis {

  static types = {
    Z_TOP: "z_top",
    Z_TOP_X_FORWARD: "z_top_x_forward",
    Z_BOTTOM: "z_bottom",
    Y_TOP: "y_top",
    Y_TOP_Z_LEFT: "y_top_z_left",
    Y_TOP_Z_FORWARD: "y_top_z_forward",
    Y_TOP_2D: "y_top_2d",
    Z_TOP_X_RIGHT_2D: "z_top_x_right_2d",
    Z_TOP_X_LEFT_2D: "z_top_x_left_2d",
    Z_TOP_Y_RIGHT_2D: "z_top_y_right_2d",
    X_TOP_Y_LEFT_2D: "x_top_y_left_2d"
  };

  constructor(obj = defaultObj) {
    this.layerHtml = new LayerHTML();

    this.siblingCamera = obj.siblingCamera;

    this.scene = new THREE.Scene();

    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(obj.width, obj.height);
    this.renderer.setClearColor(0x000000, 0);

    this.lightOn();

    const zoom = 3;

    this.camera = new THREE.OrthographicCamera(
      obj.width / -zoom, obj.width / zoom, obj.height / zoom, obj.height / -zoom,
      1, 10000
    );
    if (this.siblingCamera && this.siblingCamera.up) {
      this.camera.up = this.siblingCamera.up;
    }

    this.scene.add(this.camera);

    this.object = new THREE.Object3D();

    this.height = 25;

    this.hafHeight = this.height / 2;
    this.padding = 3;

    this.scene.add(this.object);

    /// AndreiP: DEBUG
    // this.scene.add(new THREE.AxesHelper(100));


    // this.setAxisOrientation(obj.orientation);
  }

  setAxisOrientation(type) {
    for (let i = this.object.children.length - 1; i >= 0; i--) {
      this.object.remove(this.object.children[i]);
    }

    this.layerHtml.clear();

    const axisGroup = new THREE.Object3D();
    const xAxis = this.createAxis('X', BLUE);
    xAxis.object.rotation.x = Math.PI / -2;
    xAxis.object.position.set(0, -this.hafHeight, -this.hafHeight);
    axisGroup.add(xAxis.object);

    const yAxis = this.createAxis('Y', GREEN);
    yAxis.object.rotation.z = Math.PI / 2;
    yAxis.object.position.set(-this.hafHeight, -this.hafHeight, 0);
    axisGroup.add(yAxis.object);

    const zAxis = this.createAxis('Z', RED);
    axisGroup.add(zAxis.object);

    // VladP: DEBUG
    // type = Axis.types.X_TOP_Y_LEFT_2D; //eslint-disable-line
    switch (type) {
      case Axis.types.Y_TOP: 
      case Axis.types.Y_TOP_2D: {
        axisGroup.rotation.z = -Math.PI / 2;
        axisGroup.position.x += this.hafHeight; 
        if (type === Axis.types.Y_TOP_2D) // move the group in front of camera
          axisGroup.position.x -= this.height;

        xAxis.label.position.set(
          xAxis.object.position.x - (type === Axis.types.Y_TOP_2D ? this.height : 0),
          xAxis.object.position.y + this.hafHeight,
          xAxis.object.position.z - this.hafHeight - conusSize - this.padding
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x + this.hafHeight - (type === Axis.types.Y_TOP_2D ? this.height : 0),
          yAxis.object.position.y + this.height + 2 * conusSize, // + this.padding,
          yAxis.object.position.z
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x + this.height + conusSize + this.padding - (type === Axis.types.Y_TOP_2D ? this.height : 0),
          zAxis.object.position.y - (type === Axis.types.Y_TOP_2D ? 2 * this.padding : 0),
          zAxis.object.position.z + (type === Axis.types.Y_TOP_2D ? 2 * this.padding : 0)
        );
        this.layerHtml.add(zAxis.label);

        // if (type === Axis.types.Y_TOP_2D) // move the arrow in front of camera
        //   zAxis.object.position.y -= this.height + conusSize / 3;
        break;
      }
      case Axis.types.Y_TOP_Z_LEFT: {
        axisGroup.rotation.z = -Math.PI / 2;
        axisGroup.rotation.y = -Math.PI / 2;
        axisGroup.position.z += this.hafHeight; 

        xAxis.label.position.set(
          zAxis.object.position.x + this.height + conusSize + this.padding,
          zAxis.object.position.y,
          zAxis.object.position.z
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x + this.hafHeight,
          yAxis.object.position.y + this.height + 2 * conusSize, // + this.padding,
          yAxis.object.position.z
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          xAxis.object.position.x,
          xAxis.object.position.y + this.hafHeight,
          xAxis.object.position.z + this.height + this.hafHeight + conusSize + this.padding
        );
        this.layerHtml.add(zAxis.label);

        break;
      }
      case Axis.types.Y_TOP_Z_FORWARD: {
        axisGroup.rotation.z = -Math.PI / 2;
        axisGroup.rotation.y = -Math.PI;
        axisGroup.position.x = -this.hafHeight; 

        xAxis.label.position.set(
          xAxis.object.position.x,
          xAxis.object.position.y + this.hafHeight,
          xAxis.object.position.z + this.height + this.hafHeight + conusSize + this.padding
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x + this.hafHeight,
          yAxis.object.position.y + this.height + 2 * conusSize, // + this.padding,
          yAxis.object.position.z
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x - this.height - conusSize - this.padding,
          zAxis.object.position.y,
          zAxis.object.position.z
        );
        this.layerHtml.add(zAxis.label);

        break;
      }
      case Axis.types.Z_BOTTOM: {
        axisGroup.rotation.z = -Math.PI;
        axisGroup.position.y -= this.hafHeight; 

        xAxis.label.position.set(
          xAxis.object.position.x,
          xAxis.object.position.y + this.hafHeight,
          xAxis.object.position.z - this.hafHeight - conusSize - this.padding
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x + this.height + 2 * conusSize,
          yAxis.object.position.y + this.hafHeight,
          yAxis.object.position.z
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x,
          zAxis.object.position.y - (this.height + conusSize + this.padding),
          zAxis.object.position.z
        );
        this.layerHtml.add(zAxis.label);
        break;
      }
      case Axis.types.Z_TOP_X_LEFT_2D: {
        axisGroup.rotation.y += Math.PI;
        axisGroup.position.y += this.hafHeight; 
        // move the group in front of camera
        axisGroup.position.x -= this.height;

        xAxis.label.position.set(
          xAxis.object.position.x - this.height,
          xAxis.object.position.y + this.hafHeight,
          xAxis.object.position.z + this.height + 2 * conusSize
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x + this.hafHeight + conusSize + this.padding,
          yAxis.object.position.y + this.hafHeight - 2 * this.padding,
          yAxis.object.position.z - 2 * this.padding
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x - this.height,
          zAxis.object.position.y + this.height + conusSize + this.padding,
          zAxis.object.position.z
        );
        this.layerHtml.add(zAxis.label);
        break;
      }
      case Axis.types.Z_TOP_Y_RIGHT_2D: {
        axisGroup.rotation.y -= Math.PI/2;
        axisGroup.position.y += this.hafHeight; 
        // move the group in front of camera
        axisGroup.position.x -= this.height;

        xAxis.label.position.set(
          xAxis.object.position.x + conusSize + this.padding,
          xAxis.object.position.y + this.hafHeight - 2 * this.padding,
          xAxis.object.position.z + this.hafHeight + 2 * this.padding
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x - this.hafHeight,
          yAxis.object.position.y + this.hafHeight,
          yAxis.object.position.z - this.height - conusSize - this.padding,
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x - this.height,
          zAxis.object.position.y + this.height + conusSize + this.padding,
          zAxis.object.position.z
        );
        this.layerHtml.add(zAxis.label);

        // xAxis.object.position.z += this.height + conusSize / 3;
        break;
      }
      case Axis.types.X_TOP_Y_LEFT_2D: {
        axisGroup.rotation.x += Math.PI/2;
        axisGroup.rotation.z -= Math.PI/2;
        // move the group in front of camera
        axisGroup.position.x -= this.height;

        xAxis.label.position.set(
          xAxis.object.position.x - this.height - conusSize + this.padding,
          xAxis.object.position.y + 3 * this.hafHeight + conusSize + this.padding,
          xAxis.object.position.z + this.hafHeight
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x - this.height,
          yAxis.object.position.y + this.hafHeight,
          yAxis.object.position.z + this.height + conusSize + this.padding,
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x - this.hafHeight + conusSize + this.padding,
          zAxis.object.position.y - 2 * this.padding,
          zAxis.object.position.z - 2 * this.padding
        );
        this.layerHtml.add(zAxis.label);

        break;
      }
      case Axis.types.Z_TOP_X_FORWARD: {
        axisGroup.rotation.y += Math.PI/2;
        axisGroup.position.y += this.hafHeight; 

        xAxis.label.position.set(
          yAxis.object.position.x - this.hafHeight - conusSize - this.padding,
          yAxis.object.position.y + this.hafHeight,
          yAxis.object.position.z 
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          xAxis.object.position.x,
          xAxis.object.position.y + this.hafHeight,
          -xAxis.object.position.z + this.hafHeight + conusSize + this.padding
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x,
          zAxis.object.position.y + this.height + conusSize + this.padding,
          zAxis.object.position.z
        );
        this.layerHtml.add(zAxis.label);
        break;
      }
      case Axis.types.Z_TOP:
      case Axis.types.Z_TOP_X_RIGHT_2D:
      default: {
        axisGroup.position.y += this.hafHeight; 

        xAxis.label.position.set(
          xAxis.object.position.x,
          xAxis.object.position.y + this.hafHeight,
          xAxis.object.position.z - this.hafHeight - conusSize - this.padding
        );
        this.layerHtml.add(xAxis.label);

        yAxis.label.position.set(
          yAxis.object.position.x - this.hafHeight - conusSize - this.padding,
          yAxis.object.position.y + this.hafHeight - (type === Axis.types.Z_TOP_X_RIGHT_2D ? 2 * this.padding : 0),
          yAxis.object.position.z + (type === Axis.types.Z_TOP_X_RIGHT_2D ? 2 * this.padding : 0)
        );
        this.layerHtml.add(yAxis.label);

        zAxis.label.position.set(
          zAxis.object.position.x,
          zAxis.object.position.y + this.height + conusSize + this.padding,
          zAxis.object.position.z
        );
        this.layerHtml.add(zAxis.label);
        break;
      }
    }

    this.object.add(axisGroup);
  }

  createAxis(name, color) { // eslint-disable-line class-methods-use-this
    const object = new THREE.Object3D();

    const width = 2;

    const material = new THREE.MeshLambertMaterial({
      color: window.parseInt(color)
    });

    // AndreiP: there's no need to discretize it in 250 small segments (it's only having a width of 2!)
    const cylinderGeometry = new THREE.CylinderGeometry(width, width, this.height);
    //const cylinderGeometry = new THREE.CylinderGeometry(width, width, this.height, this.height * 10);

    object.add(new THREE.Mesh(cylinderGeometry, material));

    const coneGeometry = new THREE.ConeGeometry(width * 2, conusSize, this.height);
    const cone = new THREE.Mesh(coneGeometry, material);
    cone.position.y = this.height - 5;
    object.add(cone);

    const label = new HTMLLabel({
      text: name,
      align: HTMLLabel.types.align.CENTER,
      verticalAlign: HTMLLabel.types.verticalAlign.CENTER,
      color: color.replace("0x", "#")
    });
    label.position.set(
      object.position.x,
      object.position.y,
      object.position.z
    );

    return {
      object,
      label
    };
  }

  setSiblingCamera(camera) {
    this.siblingCamera = camera;
  }

  animate() {
    if (this.siblingCamera && this.siblingCamera.position) {
      var cameraDir = new THREE.Vector3();
      cameraDir.copy(this.siblingCamera.position).normalize();
      this.camera.position.set(0, 0, 0);
      this.camera.position.addScaledVector(cameraDir, 100);
      // this.camera.position.copy(this.siblingCamera.position);
    } else {
      this.camera.position.copy(new THREE.Vector3(10, 0, 0));
    }

    this.layerHtml.render(this.camera, this.renderer);

    this.camera.lookAt(this.scene.position);

    this.renderer.render(this.scene, this.camera);
  }

  get domElement() {
    if (!this._viewHTMLElement) {
      this._viewHTMLElement = document.createElement('div');
      this._viewHTMLElement.appendChild(this.renderer.domElement);
      this._viewHTMLElement.appendChild(this.layerHtml.domElement);
    }

    return this._viewHTMLElement;
  }

  lightOn() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0xffffff, 0.8, 5000);
    pointLight.position.set(900, 600, 800);
    this.scene.add(pointLight);
  }

}



// WEBPACK FOOTER //
// ./src/object-view/axis/index.js